import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '../app/hooks';
import { selectCurrentUser, selectIsAuthenticated } from '../features/auth/authSlice';

interface ProtectedRouteProps {
    allowedRoles?: string[];
    requireMasterAdmin?: boolean;
    // Permission code(s) (e.g. 'rostering:view') that also grant access, checked
    // as an OR against allowedRoles - since role is a single collapsed
    // admin/employee label (see authService), a manager/supervisor granted a
    // specific permission via Security Center would otherwise never pass a
    // role-only check. Pass an array to accept ANY one of several permissions.
    requiredPermission?: string | string[];
    // Role code(s) to hard-block regardless of clearance or permissions - the
    // ONLY way to keep a specific admin-tier custom role (e.g.
    // 'department_head') off a sensitive page, since clearance<=5 otherwise
    // unconditionally bypasses allowedRoles for ANY admin-tier role (see
    // below), and requiredPermission can only ever grant extra access, never
    // take it away. Checked before every other check.
    excludeRoles?: string[];
}

const ProtectedRoute = ({ allowedRoles, requireMasterAdmin, requiredPermission, excludeRoles }: ProtectedRouteProps) => {
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const user = useAppSelector(selectCurrentUser);
    const location = useLocation();

    // Check authentication first
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Hard block first, independent of clearance/permissions.
    if (excludeRoles && excludeRoles.length > 0) {
        const realRoles = (user?.roles || []).map(r => r.toLowerCase());
        const normalizedExcluded = excludeRoles.map(r => r.toLowerCase());
        if (realRoles.some(r => normalizedExcluded.includes(r))) {
            return <Navigate to="/unauthorized" replace />;
        }
    }

    // Check master admin authorization
    if (requireMasterAdmin) {
        if (!user?.isMasterAdmin) {
            return <Navigate to="/dashboard" replace />;
        }
    }

    // Check role authorization if roles are specified
    if (allowedRoles && allowedRoles.length > 0) {
        // `user.role` is a collapsed 'admin' | 'employee' label (see
        // authService.ts) that is always 'admin' for any clearance<=5 user -
        // checking it against allowedRoles meant every admin-viewMode user
        // passed regardless of which specific role they actually hold, so a
        // role with only a few permission boxes ticked in Security Center
        // still reached routes it had no business reaching. `user.roles`
        // (plural) is the real array of role codes - use that instead.
        //
        // BUT: allowedRoles here is always one of two hardcoded constants -
        // ADMIN_ROLES (5 built-in codes) or ALL_ROLES (those + 'employee').
        // A CUSTOM role created in Security Center (e.g. 'department_head')
        // is never literally in either list, so a pure role-code match alone
        // would lock every custom role out of the whole admin dashboard,
        // including routes with no permission alternative at all (Dashboard
        // itself). Restore the same two escape hatches the rest of the app
        // already uses to recognize a custom role as admin-tier / open-to-all:
        //  - ALL_ROLES always includes 'employee' (ADMIN_ROLES never does) -
        //    that's the signal a route is meant to be reachable by literally
        //    any authenticated user, regardless of role.
        //  - clearance_level <= 5 is the same admin-tier threshold Sidebar
        //    and authSlice's viewMode already use to treat a custom role as
        //    "admin", independent of its specific role code.
        const realRoles = (user?.roles || []).map(r => r.toLowerCase());
        const clearanceLevel = user?.clearanceLevel ?? 10;
        const openToEveryone = allowedRoles.includes('employee');
        const roleAllowed = openToEveryone
            || realRoles.some(r => allowedRoles.includes(r))
            || (clearanceLevel > 0 && clearanceLevel <= 5);

        const requiredPermissions = requiredPermission
            ? (Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission])
            : [];
        const permissionAllowed = requiredPermissions.length > 0
            && !!user?.permissions?.some(p => requiredPermissions.includes(p));

        if (!roleAllowed && !permissionAllowed) {
            return <Navigate to="/unauthorized" replace />;
        }
    }

    return <Outlet />;
};

export default ProtectedRoute;
